import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, FormControl, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { TechniqueKey, VisualKind } from "../types";
import { SONIOX_VOICES } from "../lib/schemaModes";
import { createChairEngine } from "../lib/chairAudioEngine";
import { postChair, postVisual } from "../api";
import { useToast } from "../../../components/Toast";
import { PlaybackControls } from "./PlaybackControls";

interface ImageVoiceToolProps {
  patientId: string;
  sessionId?: string;
  language: "en" | "hu";
  getToken: () => Promise<string | null>;
  technique: TechniqueKey;   // sent as the clip's mode
  visualKind: VisualKind;    // "single" (symbolic) or "creature" (child-friendly)
  imageLabelKey: string;     // e.g. "experiential.otherDesc"
  wordsLabelKey: string;     // e.g. "experiential.otherWords"
  defaultVoice: string;
}

/* One rendered image plus one spoken clip — Compassionate Other, Miracle
   Question, Worry Monster, Brave Rehearsal all share this shape. */
export function ImageVoiceTool({
  patientId, sessionId, language, getToken,
  technique, visualKind, imageLabelKey, wordsLabelKey, defaultVoice,
}: ImageVoiceToolProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const [description, setDescription] = useState("");
  const [words, setWords] = useState("");
  const [voice, setVoice] = useState(defaultVoice);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [generated, setGenerated] = useState(false);
  const [looping, setLooping] = useState(false);

  const engineRef = useRef<ReturnType<typeof createChairEngine> | null>(null);

  const onGenerate = async () => {
    try {
      setBusy(true);
      await engineRef.current?.close();
      const [visual, chair] = await Promise.all([
        postVisual(getToken, {
          patient_id: patientId, session_id: sessionId, kind: visualKind, prompt: description,
        }),
        postChair(getToken, {
          patient_id: patientId,
          session_id: sessionId,
          language,
          chairs: [{ id: crypto.randomUUID(), mode: technique, label: "", text: words, voice }],
        }),
      ]);
      const engine = createChairEngine(chair.clips);
      await engine.load((url) => fetch(url).then((r) => r.arrayBuffer()));
      engineRef.current = engine;
      setImageUrl(visual.images[0].url);
      setGenerated(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg.includes("image_policy_declined") ? t("experiential.policyDeclined") : `${t("experiential.genError")}: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    return () => {
      engineRef.current?.close();
    };
  }, []);

  return (
    <div>
      <TextField
        label={t(imageLabelKey)}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={{ mb: 2 }}
      />
      <TextField
        label={t(wordsLabelKey)}
        multiline
        minRows={3}
        fullWidth
        size="small"
        value={words}
        onChange={(e) => setWords(e.target.value)}
        sx={{ mb: 2 }}
      />
      <FormControl size="small" sx={{ minWidth: 130, mb: 2 }}>
        <InputLabel>{t("experiential.readBy")}</InputLabel>
        <Select value={voice} label={t("experiential.readBy")} onChange={(e) => setVoice(e.target.value)}>
          {SONIOX_VOICES.map((v) => (
            <MenuItem key={v} value={v}>{v}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="exp-chair-controls">
        <Button
          variant="contained"
          size="small"
          onClick={onGenerate}
          disabled={busy || !description.trim() || !words.trim()}
        >
          {t("experiential.generate")}
        </Button>

        {generated && (
          <PlaybackControls
            onPlay={() => engineRef.current?.play()}
            onStop={() => engineRef.current?.stop()}
            looping={looping}
            onToggleLoop={() => {
              const next = !looping;
              setLooping(next);
              engineRef.current?.setLoop(next);
            }}
          />
        )}
      </div>

      {imageUrl && (
        <img src={imageUrl} alt={t(imageLabelKey)} style={{ width: "100%", borderRadius: 6, marginTop: 12 }} />
      )}
    </div>
  );
}
